import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaHome } from 'react-icons/fa';

const FooterWrapper = styled.footer`
  background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
  color: #ffffff;
  padding: 0;
  margin-top: 5rem;
`;

const FooterMain = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem 3rem;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 3rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
    gap: 2.5rem;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    padding: 3rem 1.5rem 2rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const CompanySection = styled.div`
  display: flex;
  flex-direction: column;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const LogoIcon = styled(FaHome)`
  font-size: 2rem;
  color: #c41e3a;
`;

const CompanyName = styled.h3`
  font-family: 'Syncopate', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
  line-height: 1.3;
`;

const CompanyInfo = styled.div`
  font-size: 0.85rem;
  line-height: 1.8;
  color: #b0b0b0;
  margin-bottom: 1.5rem;
`;

const ColumnTitle = styled.h4`
  font-family: 'Montserrat', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  color: #ffffff;
  position: relative;
  padding-bottom: 0.75rem;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: #c41e3a;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FooterLink = styled(Link)`
  color: #b0b0b0;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;

  &:hover {
    color: #ffffff;
    padding-left: 5px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: #b0b0b0;
  font-size: 0.9rem;
  line-height: 1.6;
`;

const ContactIcon = styled.span`
  color: #c41e3a;
  font-size: 1rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const ContactLink = styled.a`
  color: #b0b0b0;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffffff;
  }
`;

const PSRASection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 576px) {
    align-items: center;
  }
`;

const PSRALogo = styled.img`
  max-width: 180px;
  height: auto;
  margin-bottom: 1rem;
  background: #ffffff;
  padding: 0.75rem 1rem;
  border-radius: 4px;
`;

const PSRAText = styled.p`
  font-size: 0.8rem;
  color: #888888;
  line-height: 1.6;
`;

const FooterBottom = styled.div`
  border-top: 1px solid #3a3a3a;
  padding: 1.5rem 2rem;
  text-align: center;
`;

const Copyright = styled.p`
  font-size: 0.85rem;
  color: #888888;
  margin: 0;
  letter-spacing: 0.5px;
`;

const Footer = () => (
  <FooterWrapper>
    <FooterMain>
      <FooterColumn>
        <CompanySection>
          <LogoContainer>
            <LogoIcon />
            <CompanyName>SWIPE<br />PROPERTIES</CompanyName>
          </LogoContainer>
          <CompanyInfo>
            <strong>Swipe Lettings & Management LTD</strong><br />
            Trading as Swipe Property<br />
            CRO 603559
          </CompanyInfo>
        </CompanySection>
      </FooterColumn>

      <FooterColumn>
        <ColumnTitle>Quick Links</ColumnTitle>
        <FooterLinks>
          <FooterLink to="/">Home</FooterLink>
          <FooterLink to="/about">About Us</FooterLink>
          <FooterLink to="/properties">Properties</FooterLink>
          <FooterLink to="/services">Our Services</FooterLink>
          <FooterLink to="/blogs">Blog</FooterLink>
          <FooterLink to="/contact">Contact</FooterLink>
        </FooterLinks>
      </FooterColumn>

      <FooterColumn>
        <ColumnTitle>Contact Us</ColumnTitle>
        <ContactItem>
          <ContactIcon><FaMapMarkerAlt /></ContactIcon>
          <span>Unit 208 Nesta, Karkraft House,<br />Kilbarrack Parade, Dublin 5,<br />D05DC60</span>
        </ContactItem>
        <ContactItem>
          <ContactIcon><FaPhone /></ContactIcon>
          <ContactLink href="tel:015252353">01 525 2353</ContactLink>
        </ContactItem>
        <ContactItem>
          <ContactIcon><FaEnvelope /></ContactIcon>
          <ContactLink href="mailto:info@swipeproperty.ie">info@swipeproperty.ie</ContactLink>
        </ContactItem>
      </FooterColumn>

      <FooterColumn>
        <PSRASection>
          <ColumnTitle>Regulated By</ColumnTitle>
          <PSRALogo src="/psra-logo.png" alt="PSRA - Property Services Regulatory Authority" />
          <PSRAText>
            PSR Registration Number:<br />
            <strong>003920</strong>
          </PSRAText>
        </PSRASection>
      </FooterColumn>
    </FooterMain>

    <FooterBottom>
      <Copyright>
        &copy; {new Date().getFullYear()} Swipe Lettings & Management LTD. All rights reserved.
      </Copyright>
    </FooterBottom>
  </FooterWrapper>
);

export default Footer;
